import { usePageDrawer } from '@app/components/page/drawer/PageDrawerContext';
import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { pushTo } from '@app/core/routing';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useAllowFragmentExternalExport } from '@app/modules/fragments/hooks/useAllowFragmentExternalExport';
import { useFragmentCreate } from '@app/modules/fragments/hooks/useFragmentCreate';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { CreateFragmentForm } from '@app/modules/fragments/screens/add/CreateFragmentForm';
import { useFragmentCreationStore } from '@app/modules/fragments/stores/fragment-creation.store';
import { useCallback } from 'react';
import { CreateFragmentFormValues } from '../../../../../../fragments/screens/add/useFragmentForm';
import { useFragmentEditorStore } from '../../stores/useFragmentEditorStore';

/**
 * Form component for creating a joint fragment from the editor.
 * Uses the active station context and the fragment editor store.
 */
export const CreateJointFragmentFromEditor = () => {
  const { closeDrawer } = usePageDrawer();
  const fragment = useFragmentEditorStore((state) => state.fragment);
  const setDraft = useFragmentEditorStore((state) => state.setDraft);
  const exportEnabled = useFragmentCreationStore((state) => state.exportEnabled);
  const capture = usePostHogCapture({ domain: 'create_fragment' });

  const station = useActiveStation();
  const allowExternalExport = useAllowFragmentExternalExport(station);

  const create = useFragmentCreate({
    onSuccess: (result) => {
      if (!fragment) return;

      const shouldNavigate = useFragmentCreationStore.getState().navigateAfterCreate;
      if (shouldNavigate) {
        pushTo(routeGenerator.view(result.id));
        closeDrawer();
        return;
      }

      // Preserve the displayed chunks
      setDraft({
        ...result,
        chunks: fragment.chunks,
      });
      closeDrawer();
    },
  });

  const handleCancel = useCallback(() => {
    capture('discard', { station_id: station.id, type: 'JOINT_FRAGMENT' });
    closeDrawer();
  }, [capture, station.id, closeDrawer]);

  if (!fragment) {
    return null;
  }

  async function handleCreate(data: CreateFragmentFormValues) {
    if (!fragment) return;

    const { doExternalExport, platformUpload, ...rest } = {
      ...fragment,
      ...data,
    };

    const saveData = {
      ...rest,
      type: 'JOINT_FRAGMENT' as const,
      stationId: station.id,
      tags: data.tags,
      chunks: fragment.chunks.map((c) => ({
        from: c.from,
        to: c.to,
        duration: c.duration,
        label: c.label,
        highlights: c.highlights ?? [],
      })),
      doExternalExport,
      platformUpload,
    };

    await create(saveData);
    capture('save', {
      type: 'JOINT_FRAGMENT',
      station_id: station.id,
      chunk_count: fragment.chunks.length,
    });
  }

  return (
    <CreateFragmentForm
      station={station}
      initialValues={{
        doExternalExport: allowExternalExport && exportEnabled,
      }}
      allowExternalExport={allowExternalExport}
      onCreate={handleCreate}
      onCancel={handleCancel}
    />
  );
};
